import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Calendar, Phone, Edit3, Search, PhoneCall, DollarSign, TrendingUp, Shield, Users, Clock, Award, Heart, Handshake } from "lucide-react";
import Logo from "../components/app/Logo";
import { useAuth } from "../context/AuthContext";
import { COMPANY_PHONE, COMPANY_PHONE_TEL, CALENDLY_URL } from "../lib/brand";

const HERO_IMG = "https://images.unsplash.com/photo-1758518729240-7162d07427b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxoZWFsdGhjYXJlJTIwYnVzaW5lc3MlMjBwcm9mZXNzaW9uYWwlMjBoYW5kc2hha2V8ZW58MHx8fHwxNzc3NDAyODIxfDA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { Icon: Edit3, title: "Submit the Referral", desc: "Send us the case details in under 5 minutes." },
  { Icon: Search, title: "We Review the Case", desc: "Our specialists evaluate every angle for placement." },
  { Icon: PhoneCall, title: "Client Communication", desc: "We handle outreach and protect your relationship." },
  { Icon: DollarSign, title: "You Get Paid", desc: "Compensation flows on issued and paid business." },
];

const TIERS = [
  { name: "TIER 1", label: "Healthy Declines", rate: "$150 → $300/case",
    details: "Controlled hypertension, mild sleep apnea, ADHD on meds. Money leads. Tops out at 40% commission share." },
  { name: "TIER 2", label: "Complex Cases", rate: "$100 → $200/case",
    details: "Multiple Rx, past surgeries now stable, mixed histories. Workable with explanation." },
  { name: "TIER 3", label: "Hard to Place", rate: "$50 → $100/case",
    details: "Pregnancy, diabetes, COPD, cancer history. Still placed. Still paid. $100 absolute cap." },
];

const REASONS = [
  { Icon: TrendingUp, text: "Recover revenue from declined cases" },
  { Icon: Shield, text: "Protect your client relationship" },
  { Icon: Users, text: "Specialist team handles placement" },
  { Icon: Clock, text: "Save hours per case" },
  { Icon: Award, text: "Pay scales with your volume" },
  { Icon: Heart, text: "True partnership mentality" },
];

const STATS = [
  { v: "$150+", l: "Per Case" },
  { v: "40%", l: "Top Share" },
  { v: "3", l: "Tiers" },
  { v: "100%", l: "Placed" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center"><div className="w-10 h-10 border-4 rounded-full animate-spin" style={{ borderColor: "#D62828", borderTopColor: "transparent" }} /></div>;
  }

  return (
    <div className="bg-white">
      {/* Header */}
      <header className="border-b sticky top-0 z-30 bg-white/90 backdrop-blur" style={{ borderColor: "#E8E8E8" }}>
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
          <Logo compact />
          <button data-testid="header-signin-btn" onClick={() => navigate("/login")} className="btn-primary !px-5 !py-2.5 text-sm">Sign In</button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 -z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <span className="inline-block text-[11px] font-extrabold tracking-[2px] text-white/95 px-3 py-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.18)" }}>
              FOR REFERRAL PARTNERS
            </span>
            <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-black mt-5 leading-[1.05]">
              Turn Declined Cases Into Revenue
            </h1>
            <p className="text-white/80 font-extrabold tracking-[2px] uppercase text-xs sm:text-sm mt-4">SMARTER SOLUTIONS, HEALTHIER FUTURES.</p>
            <p className="text-white/95 mt-5 max-w-lg leading-relaxed text-base sm:text-lg">
              Submit your hardest health insurance cases — we place them and you get paid on issued business.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button data-testid="hero-cta-submit" onClick={() => navigate("/login")} className="btn-primary text-base">
                Submit a Referral <ArrowRight size={18} />
              </button>
              <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="hero-cta-call" className="inline-flex items-center justify-center gap-2 rounded-full font-bold text-sm px-6 py-3 border-2 text-white" style={{ borderColor: "rgba(255,255,255,0.6)" }}>
                <Calendar size={16} /> Schedule a Partner Call
              </a>
            </div>
          </div>
          <div className="hidden lg:block" />
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-5 -mt-12 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {STATS.map((s, i) => (
            <div key={i} className={`bg-white rounded-2xl border shadow-sm p-5 text-center fade-up fade-up-${i+1}`} style={{ borderColor: "#E8E8E8" }}>
              <div className="font-display text-2xl sm:text-3xl font-black tnum" style={{ color: "#D62828" }}>{s.v}</div>
              <div className="text-[10px] uppercase tracking-wider font-extrabold mt-1" style={{ color: "#8A8A8A" }}>{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 py-20 sm:py-24">
        <span className="eyebrow">HOW IT WORKS</span>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black mt-2 mb-3">From Declined to Paid in 4 Steps</h2>
        <div className="grid md:grid-cols-2 gap-4 mt-8">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center gap-5 bg-white rounded-2xl p-6 border" style={{ borderColor: "#E8E8E8" }}>
              <span className="font-display text-2xl font-black w-10 shrink-0" style={{ color: "#D62828" }}>{String(i+1).padStart(2, "0")}</span>
              <div className="w-12 h-12 rounded-full grid place-items-center shrink-0" style={{ background: "#E3F0FB" }}>
                <s.Icon size={20} color="#D62828" />
              </div>
              <div>
                <h3 className="font-extrabold text-base">{s.title}</h3>
                <p className="text-sm mt-1" style={{ color: "#5A5A5A" }}>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Compensation */}
      <section className="bg-[#FAFAFA] py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-5">
          <span className="eyebrow">COMPENSATION</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black mt-2 mb-3">Better Leads. Higher Pay.</h2>
          <p className="max-w-2xl text-[15px]" style={{ color: "#5A5A5A" }}>
            Volume scales your rate per case in a rolling calendar month. Top partners hit 40% commission share.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-8">
            {TIERS.map((t, i) => (
              <div key={i} className={`bg-white rounded-2xl p-6 border-2`} style={{ borderColor: i === 0 ? "#D62828" : "#E8E8E8" }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs tracking-[1.5px] font-extrabold" style={{ color: i === 0 ? "#D62828" : "#1A1A1A" }}>{t.name}</span>
                  <span className="pill" style={{ background: i === 0 ? "#D62828" : "#E3F0FB", color: i === 0 ? "#fff" : "#D62828" }}>{t.rate}</span>
                </div>
                <h3 className="font-display text-2xl font-black mt-3">{t.label}</h3>
                <p className="text-sm mt-2 leading-relaxed" style={{ color: "#5A5A5A" }}>{t.details}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="max-w-7xl mx-auto px-5 py-20 sm:py-24">
        <span className="eyebrow">WHY PARTNERS CHOOSE US</span>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black mt-2 mb-8">Built for Producers Like You</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {REASONS.map((r, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 border" style={{ borderColor: "#E8E8E8" }}>
              <div className="w-10 h-10 rounded-full grid place-items-center" style={{ background: "#E3F0FB" }}>
                <r.Icon size={18} color="#D62828" />
              </div>
              <p className="font-bold text-sm mt-3">{r.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 pb-16">
        <div className="rounded-3xl p-10 sm:p-14 text-center" style={{ background: "#A91D1D" }}>
          <Handshake size={40} color="#E3F0FB" className="mx-auto" />
          <h3 className="font-display text-white text-2xl sm:text-3xl lg:text-4xl font-black mt-4">Ready to send your first case?</h3>
          <p className="text-white/85 mt-2 text-base">Create your partner account in 30 seconds.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button data-testid="band-register-btn" onClick={() => navigate("/register")} className="rounded-full px-7 py-3.5 font-black text-sm bg-white" style={{ color: "#D62828" }}>
              Become a Partner
            </button>
            <button data-testid="band-signin-btn" onClick={() => navigate("/login")} className="rounded-full px-7 py-3.5 font-bold text-sm text-white underline">
              Already a partner? Sign in
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-5 py-12" style={{ background: "#1F2A3A" }}>
        <div className="max-w-7xl mx-auto">
          <div className="bg-white inline-flex p-3 rounded-2xl"><Logo compact /></div>
          <p className="mt-4 text-[11px] uppercase tracking-[2px] font-extrabold" style={{ color: "#E3F0FB" }}>Smarter Solutions, Healthier Futures</p>
          <p className="mt-2 text-sm max-w-md" style={{ color: "rgba(255,255,255,0.7)" }}>Specialized case placement for referral partners.</p>
          <div className="mt-4 flex flex-col gap-2">
            <a data-testid="footer-call-btn" href={`tel:${COMPANY_PHONE_TEL}`} className="inline-flex items-center gap-2 text-sm font-bold" style={{ color: "#E3F0FB" }}>
              <Phone size={14} /> {COMPANY_PHONE}
            </a>
            <a data-testid="footer-calendly-btn" href={CALENDLY_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-bold" style={{ color: "#E3F0FB" }}>
              <Calendar size={14} /> Schedule a Partner Call
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
